import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Stack,
  FormHelperText
} from '@mui/material';
import { db } from '../../firebase/config';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc, getDoc, addDoc, where } from 'firebase/firestore';
import { notifyFarmer } from '../../services/notificationService';
import { Cloudinary } from '@cloudinary/url-gen';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../../utils/cloudinaryConfig';
import { Edit, Trash, Upload, X, Plus } from 'lucide-react';
import { serverTimestamp } from 'firebase/firestore';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';

/**
 * Composant pour gérer les annonces des agriculteurs
 */
const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  // États pour la création d'annonces par les admins
  const [farmers, setFarmers] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // États pour le formulaire d'édition d'annonce
  const [openAnnouncementDialog, setOpenAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    description: '',
    price: '',
    quantity: '',
    unit: 'kg',
    category: '',
    imageUrls: []
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Initialisation de Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: CLOUDINARY_CLOUD_NAME
    }
  });

  // Récupérer toutes les annonces et les agriculteurs
  useEffect(() => {
    fetchAnnouncements();
    fetchFarmers();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const announcementsQuery = query(
        collection(db, 'announcements'),
        orderBy('createdAt', 'desc')
      );
      
      const announcementsSnapshot = await getDocs(announcementsQuery);
      const announcementsList = announcementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setAnnouncements(announcementsList);
    } catch (err) {
      console.error('Erreur lors de la récupération des annonces:', err);
      setError('Erreur lors de la récupération des annonces');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer la liste des agriculteurs approuvés
  const fetchFarmers = async () => {
    try {
      const farmersQuery = query(
        collection(db, 'farmers'),
        where('status', '==', 'approved'),
        orderBy('name', 'asc')
      );
      
      const farmersSnapshot = await getDocs(farmersQuery);
      const farmersList = farmersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFarmers(farmersList);
    } catch (err) {
      console.error('Erreur lors de la récupération des agriculteurs:', err);
      setError('Erreur lors de la récupération des agriculteurs');
    }
  };

  // Ajouter l'import pour le service de notification
  // Supprimer la ligne d'import ici car elle est déjà en haut du fichier
  // import { notifyFarmer } from '../../services/notificationService';
  
  // Dans la fonction handleApproveAnnouncement
  const handleApproveAnnouncement = async (id) => {
    try {
      setLoading(true);
      
      // Récupérer les informations de l'annonce
      const announcementDoc = await getDoc(doc(db, 'announcements', id));
      if (!announcementDoc.exists()) {
        throw new Error("L'annonce n'existe pas");
      }
      
      const announcementData = announcementDoc.data();
      
      // Mettre à jour le statut
      await updateDoc(doc(db, 'announcements', id), {
        status: 'approved',
        updatedAt: new Date()
      });
      
      // Envoyer une notification à l'agriculteur
      await notifyFarmer(
        announcementData.farmerId,
        id,
        announcementData.title,
        'approved'
      );
      
      setSuccess('Annonce approuvée avec succès');
      fetchAnnouncements();
    } catch (err) {
      console.error('Erreur lors de l\'approbation de l\'annonce:', err);
      setError('Erreur lors de l\'approbation de l\'annonce');
    } finally {
      setLoading(false);
    }
  };
  
  // Dans la fonction handleRejectAnnouncement
  const handleRejectAnnouncement = async (id) => {
    try {
      setLoading(true);
      
      // Récupérer les informations de l'annonce
      const announcementDoc = await getDoc(doc(db, 'announcements', id));
      if (!announcementDoc.exists()) {
        throw new Error("L'annonce n'existe pas");
      }
      
      const announcementData = announcementDoc.data();
      
      // Mettre à jour le statut
      await updateDoc(doc(db, 'announcements', id), {
        status: 'rejected',
        updatedAt: new Date()
      });
      
      // Envoyer une notification à l'agriculteur
      await notifyFarmer(
        announcementData.farmerId,
        id,
        announcementData.title,
        'rejected'
      );
      
      setSuccess('Annonce rejetée avec succès');
      fetchAnnouncements();
    } catch (err) {
      console.error('Erreur lors du rejet de l\'annonce:', err);
      setError('Erreur lors du rejet de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async () => {
    if (!selectedAnnouncement) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'announcements', selectedAnnouncement.id));
      setSuccess('Annonce supprimée avec succès');
      setOpenDialog(false);
      fetchAnnouncements();
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'annonce:', err);
      setError('Erreur lors de la suppression de l\'annonce');
    } finally {
      setLoading(false);
      setSelectedAnnouncement(null);
    }
  };

  const openDeleteDialog = (announcement) => {
    setSelectedAnnouncement(announcement);
    setOpenDialog(true);
  };

  // Fonction pour ouvrir le dialogue de création d'annonce par l'admin
  const handleCreateAnnouncementDialog = () => {
    setIsCreating(true);
    setEditingAnnouncement(null);
    setSelectedFarmer('');
    setAnnouncementForm({
      title: '',
      description: '',
      price: '',
      quantity: '',
      unit: 'kg',
      category: '',
      imageUrls: []
    });
    setOpenAnnouncementDialog(true);
  };

  // Fonction pour ouvrir le dialogue d'édition d'annonce
  const handleOpenAnnouncementDialog = (announcement) => {
    setIsCreating(false);
    setEditingAnnouncement(announcement.id);
    setAnnouncementForm({
      title: announcement.title,
      description: announcement.description,
      price: announcement.price,
      quantity: announcement.quantity,
      unit: announcement.unit || 'kg',
      category: announcement.category || '',
      imageUrls: announcement.imageUrls || []
    });
    setOpenAnnouncementDialog(true);
  };

  // Fonction pour fermer le dialogue d'édition d'annonce
  const handleCloseAnnouncementDialog = () => {
    setOpenAnnouncementDialog(false);
    setEditingAnnouncement(null);
    setIsCreating(false);
    setSelectedFarmer('');
  };

  // Fonction pour gérer les changements dans le formulaire d'annonce
  const handleAnnouncementChange = (e) => {
    const { name, value } = e.target;
    setAnnouncementForm({
      ...announcementForm,
      [name]: value
    });
  };

  // Fonction pour soumettre les modifications d'une annonce ou créer une nouvelle annonce
  const handleSubmitAnnouncement = async () => {
    try {
      setLoading(true);
      
      // Validation pour la création d'annonce par l'admin
      if (isCreating && !selectedFarmer) {
        setError('Veuillez sélectionner un agriculteur');
        setLoading(false);
        return;
      }
      
      const announcementData = {
        ...announcementForm,
        price: parseFloat(announcementForm.price) || 0,
        quantity: parseFloat(announcementForm.quantity) || 0,
        updatedAt: serverTimestamp(),
        imageUrls: announcementForm.imageUrls || []
      };
      
      if (isCreating) {
        // Création d'une nouvelle annonce par l'admin
        const selectedFarmerData = farmers.find(f => f.id === selectedFarmer);
        if (!selectedFarmerData) {
          setError('Agriculteur sélectionné introuvable');
          setLoading(false);
          return;
        }
        
        const newAnnouncementData = {
          ...announcementData,
          farmerId: selectedFarmer,
          farmerName: selectedFarmerData.name,
          farmName: selectedFarmerData.farmName || '',
          location: selectedFarmerData.location || '',
          status: 'approved', // Approbation automatique pour les annonces créées par les admins
          createdAt: serverTimestamp(),
          createdBy: 'admin'
        };
        
        await addDoc(collection(db, 'announcements'), newAnnouncementData);
        setSuccess('Annonce créée avec succès pour ' + selectedFarmerData.name);
      } else {
        // Mise à jour d'une annonce existante
        await updateDoc(doc(db, 'announcements', editingAnnouncement), {
          ...announcementData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Annonce mise à jour avec succès');
      }
      
      handleCloseAnnouncementDialog();
      fetchAnnouncements();
    } catch (err) {
      console.error('Erreur lors de la soumission de l\'annonce:', err);
      setError('Erreur lors de la soumission de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour télécharger une image
  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      setImageUploading(true);
      
      // Créer un objet FormData pour l'upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);
      
      // Upload vers Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur Cloudinary:', errorData);
        throw new Error(`Erreur d'upload: ${errorData.error?.message || 'Problème de connexion à Cloudinary'}`);
      }
      
      const data = await response.json();
      
      if (data.secure_url) {
        // Limiter à 3 images maximum
        setAnnouncementForm(prev => {
          const newImageUrls = [...prev.imageUrls];
          if (newImageUrls.length < 3) {
            newImageUrls.push(data.secure_url);
          } else {
            setError('Maximum 3 images par annonce');
          }
          return {
            ...prev,
            imageUrls: newImageUrls
          };
        });
        setSuccess('Image téléchargée avec succès');
      } else {
        throw new Error('URL sécurisée non reçue de Cloudinary');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      setError(`Erreur lors du téléchargement de l'image: ${error.message}`);
    } finally {
      setImageUploading(false);
    }
  };

  // Fonction pour supprimer une image
  const removeImage = (indexToRemove) => {
    setAnnouncementForm(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, index) => index !== indexToRemove)
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approuvée';
      case 'pending':
        return 'En attente';
      case 'rejected':
        return 'Rejetée';
      default:
        return 'Inconnu';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#014527', fontWeight: 'bold' }}>
          Gestion des annonces
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={handleCreateAnnouncementDialog}
          sx={{
            bgcolor: '#014527',
            '&:hover': { bgcolor: '#013a20' },
            borderRadius: '8px'
          }}
        >
          Créer une annonce
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ 
          p: 3, 
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Liste des annonces
          </Typography>
          
          {announcements.length > 0 ? (
            <Box>
              {/* Affichage tableau pour desktop */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Titre</TableCell>
                        <TableCell>Agriculteur</TableCell>
                        <TableCell>Prix</TableCell>
                        <TableCell>Catégorie</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {announcements.map((announcement) => (
                        <TableRow key={announcement.id}>
                          <TableCell>{announcement.title}</TableCell>
                          <TableCell>{announcement.farmerName}</TableCell>
                          <TableCell>{announcement.price} FCFA/{announcement.unit}</TableCell>
                          <TableCell>{announcement.category}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={getStatusText(announcement.status)} 
                              color={getStatusColor(announcement.status)}
                            />
                          </TableCell>
                          <TableCell>
                            {announcement.createdAt.toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleOpenAnnouncementDialog(announcement)}
                              sx={{ mr: 1 }}
                            >
                              <Edit size={18} />
                            </IconButton>
                            {announcement.status === 'pending' && (
                              <>
                                <Button 
                                  variant="contained" 
                                  color="success" 
                                  size="medium"
                                  onClick={() => handleApproveAnnouncement(announcement.id)}
                                  sx={{ mr: 1, mb: 1 }}
                                >
                                  Approuver
                                </Button>
                                <Button 
                                  variant="contained" 
                                  color="error" 
                                  size="medium"
                                  onClick={() => handleRejectAnnouncement(announcement.id)}
                                  sx={{ mb: 1 }}
                                >
                                  Rejeter
                                </Button>
                              </>
                            )}
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => openDeleteDialog(announcement)}
                            >
                              <Trash size={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              {/* Affichage en cartes pour mobile */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                {announcements.map((announcement) => (
                  <Paper 
                    key={announcement.id} 
                    elevation={1} 
                    sx={{ p: 2, borderRadius: '8px' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {announcement.title}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={getStatusText(announcement.status)} 
                        color={getStatusColor(announcement.status)}
                      />
                    </Box>
                    
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Agriculteur:
                        </Typography>
                        <Typography variant="body2">
                          {announcement.farmerName}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Prix:
                        </Typography>
                        <Typography variant="body2">
                          {announcement.price} FCFA/{announcement.unit}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Catégorie:
                        </Typography>
                        <Typography variant="body2">
                          {announcement.category}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Date:
                        </Typography>
                        <Typography variant="body2">
                          {announcement.createdAt.toLocaleDateString()}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleOpenAnnouncementDialog(announcement)}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Modifier
                      </Button>
                      {announcement.status === 'pending' && (
                        <>
                          <Button 
                            variant="contained" 
                            color="success" 
                            size="small"
                            onClick={() => handleApproveAnnouncement(announcement.id)}
                            sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.8rem' }}
                          >
                            Approuver
                          </Button>
                          <Button 
                            variant="contained" 
                            color="error" 
                            size="small"
                            onClick={() => handleRejectAnnouncement(announcement.id)}
                            sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.8rem' }}
                          >
                            Rejeter
                          </Button>
                        </>
                      )}
                      <IconButton 
                        color="error" 
                        size="medium"
                        onClick={() => openDeleteDialog(announcement)}
                      >
                        <Trash size={20} />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucune annonce disponible.
            </Typography>
          )}
        </Paper>
      )}

      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteAnnouncement} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue pour éditer une annonce */}
      <Dialog 
        open={openAnnouncementDialog} 
        onClose={handleCloseAnnouncementDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={window.innerWidth < 600} // Plein écran sur très petits écrans
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            {isCreating ? 'Créer une annonce' : 'Modifier l\'annonce'}
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleCloseAnnouncementDialog}
            aria-label="close"
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isCreating && (
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel>Sélectionner un agriculteur</InputLabel>
              <Select
                value={selectedFarmer}
                onChange={(e) => setSelectedFarmer(e.target.value)}
                label="Sélectionner un agriculteur"
                required
              >
                {farmers.map((farmer) => (
                  <MenuItem key={farmer.id} value={farmer.id}>
                    {farmer.name} - {farmer.farmName || 'Ferme'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus={!isCreating}
            margin="dense"
            name="title"
            label="Titre de l'annonce"
            type="text"
            fullWidth
            variant="outlined"
            value={announcementForm.title}
            onChange={handleAnnouncementChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={announcementForm.description}
            onChange={handleAnnouncementChange}
            required
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="price"
                label="Prix (FCFA)"
                type="number"
                fullWidth
                variant="outlined"
                value={announcementForm.price}
                onChange={handleAnnouncementChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="quantity"
                label="Quantité"
                type="number"
                fullWidth
                variant="outlined"
                value={announcementForm.quantity}
                onChange={handleAnnouncementChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                margin="dense"
                name="unit"
                label="Unité"
                fullWidth
                variant="outlined"
                value={announcementForm.unit}
                onChange={handleAnnouncementChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="kg">Kilogramme (kg)</option>
                <option value="g">Gramme (g)</option>
                <option value="l">Litre (l)</option>
                <option value="unité">Unité</option>
                <option value="sac">Sac</option>
                <option value="caisse">Caisse</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                margin="dense"
                name="category"
                label="Catégorie"
                fullWidth
                variant="outlined"
                value={announcementForm.category}
                onChange={handleAnnouncementChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Sélectionner une catégorie</option>
                <option value="legumes">Légumes</option>
                <option value="fruits">Fruits</option>
                <option value="cereales">Céréales</option>
                <option value="viande">Viande</option>
                <option value="poisson">Poisson</option>
                <option value="produits_laitiers">Produits laitiers</option>
                <option value="autre">Autre</option>
              </TextField>
            </Grid>
          </Grid>
          
          {/* Ajout du composant d'upload d'images */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Images du produit (max 3)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Affichage des images déjà téléchargées */}
              {announcementForm.imageUrls.length > 0 && (
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  {announcementForm.imageUrls.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                      <img 
                        src={url} 
                        alt={`Image ${index + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} 
                      />
                      <IconButton 
                        size="small" 
                        sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => removeImage(index)}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              
              {/* Bouton d'upload */}
              {announcementForm.imageUrls.length < 3 && (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'Téléchargement...' : 'Ajouter une image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={uploadImage}
                  />
                </Button>
              )}
              <FormHelperText>Formats acceptés: JPG, PNG. Taille max: 5MB</FormHelperText>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > button': { 
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 1, sm: 0 }
          }
        }}>
          <Button 
            onClick={handleCloseAnnouncementDialog} 
            color="inherit"
            fullWidth={window.innerWidth < 600}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSubmitAnnouncement} 
            variant="contained" 
            color="primary"
            disabled={!announcementForm.title || !announcementForm.description || !announcementForm.price || !announcementForm.quantity || !announcementForm.category}
            fullWidth={window.innerWidth < 600}
          >
            Enregistrer les modifications
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Announcements;
